import type { ButtonBaseProps } from "@mui/material/ButtonBase";
import { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import MenuList from "@mui/material/MenuList";
import ButtonBase from "@mui/material/ButtonBase";
import MenuItem, { menuItemClasses } from "@mui/material/MenuItem";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Workspace } from "../../types/workspace";
import { useOrganizationContext } from "../ContextProviders/OrganizationContext";
import { Avatar } from "@mui/material";
import { getAvatar } from "../Reuseables/CustomAvatar";


export type WorkspacesPopoverProps = ButtonBaseProps & {
  data?: Workspace[];
};

export function WorkspacesPopover({
  data = [],
  sx,
  ...other
}: WorkspacesPopoverProps) {
  const { activeWorkspace, setActiveWorkspace: setActiveWorkspace } = useOrganizationContext();
  const displayName = activeWorkspace?.displayName
    ? activeWorkspace.displayName
    : activeWorkspace.tenantName;
  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(
    null
  );
  const handleOpenPopover = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setOpenPopover(event.currentTarget);
    },
    []
  );

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);

  const handleChangeWorkspace = useCallback(
    (newValue: (typeof data)[number]) => {
      setActiveWorkspace(newValue);
      handleClosePopover();
    },
    [handleClosePopover]
  );

  const renderAvatar = (alt: string, src: string) => (
    <Box
      component="img"
      alt={alt}
      src={src}
      sx={{ width: 24, height: 24, borderRadius: "50%" }}
    />
  );

  return (
    <>
      <ButtonBase
        disableRipple
        onClick={handleOpenPopover}
        sx={{
          pl: 2,
          py: 3,
          gap: 1.5,
          pr: 1.5,
          width: 1,
          borderRadius: 1.5,
          textAlign: "left",
          justifyContent: "flex-start",
          bgcolor: "rgba(201, 201, 201, 0.2)",
          ...sx,
        }}
        {...other}
      >
        {activeWorkspace && activeWorkspace.logoSingle ? <Avatar sx={{width: 24, height: 24}} src={activeWorkspace.logoSingle}/> : getAvatar(activeWorkspace.tenantName)}
        <Box
          gap={1}
          flexGrow={1}
          display="flex"
          alignItems="center"
          sx={{ typography: "body2", fontWeight: "fontWeightSemiBold" }}
        >
          {displayName}
        </Box>

        <UnfoldMoreIcon width={18} sx={{ color: "text.disabled" }} />
      </ButtonBase>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
      >
        <MenuList
          disablePadding
          sx={{
            p: 0.5,
            gap: 0.5,
            width: 260,
            display: "flex",
            flexDirection: "column",
            [`& .${menuItemClasses.root}`]: {
              p: 1.5,
              gap: 1.5,
              borderRadius: 0.75,
              [`&.${menuItemClasses.selected}`]: {
                bgcolor: "action.selected",
                fontWeight: "fontWeightSemiBold",
              },
            },
          }}
        >
          {data?.map((option) => (
            <MenuItem
              key={option.workspaceId}
              selected={option.workspaceId === activeWorkspace?.workspaceId}
              onClick={() => handleChangeWorkspace(option)}
            >
               {option && option.logoSingle ? <Avatar src={option.logoSingle} sx={{width: 24, height: 24}}/> : getAvatar(option.tenantName)}

              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap", 
                }}
              >
                {option?.displayName ? option.displayName : option.tenantName}
              </Box>

              {/* {renderLabel(option.plan)}  */}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
}
